/*
 * aim-journey-beacon.js — forwards the storefront dataLayer to AIM growth.
 *
 * The theme already populates window.dataLayer (journey-tracker.liquid +
 * datalayer-core/datalayer-ecommerce) with rich, REAL-page-context events, but
 * nothing consumes them (no GTM is loaded on-page — GA4 runs in the Shopify
 * Customer Events pixel sandbox). This beacon is that missing consumer: it taps
 * dataLayer, keeps the events we care about, and POSTs them (batched, debounced)
 * to AIM's /api/track/journey ingest so they land in AIM growth (gd_journey_events).
 *
 * It also reads the on-page GA4 cookies (_ga / _ga_<stream>) and includes the
 * client_id + session_id so AIM can forward a small subset of CUSTOM conversion
 * events (catalogue downloads, quote requests, lead conversions) to GA4 server-side
 * via the Measurement Protocol. That subset excludes the standard ecommerce events
 * the Customer Events pixel already sends, so there is still no double-counting.
 *
 * Privacy: consent-gated (Shopify Customer Privacy `userCanBeTracked`) and sends
 * NO customer PII — customer_id / email / phone / name fields are dropped before
 * sending; journey_id / session_id are first-party random IDs, and the GA4
 * client_id is a pseudonymous analytics ID (the GA4 _ga id, or a first-party
 * fallback if _ga is absent) — not an identity. The server is authoritative
 * (re-validates shop, resolves tenant, rate-limits).
 *
 * Config is injected by snippets/aim-journey-beacon.liquid as window.__AIM_JOURNEY__
 * = { url, shop }. No-op if either is missing or the feature setting is off.
 */
(function () {
  'use strict';

  var CFG = window.__AIM_JOURNEY__ || {};
  var URL_ = CFG.url;
  var SHOP = CFG.shop;
  if (!URL_ || !SHOP) return;

  // Events worth sending to AIM growth. journey-tracker uses the `kt_journey_`
  // prefix; datalayer-ecommerce uses bare GA4 names. Everything else is ignored.
  var ECOMMERCE = {
    view_item: 1, view_item_list: 1, select_item: 1, add_to_cart: 1,
    remove_from_cart: 1, view_cart: 1, begin_checkout: 1, add_payment_info: 1,
    add_shipping_info: 1, purchase: 1, search: 1, view_promotion: 1
  };
  // Lead-conversion events fired by the theme's own snippets
  // (kt-lead-events.liquid -> phone_call, contact-form-enhanced.liquid ->
  // generate_lead). These push to the main-page dataLayer but have no other
  // reader (gtag is undefined outside the sandboxed GA4 pixel), so without this
  // allow-list every click-to-call and contact/quote submission is invisible.
  // Payloads are PII-safe: phone_number is stripped by isPii() below; the
  // generate_lead payload carries only form_name + reason.
  var LEADS = {
    phone_call: 1, generate_lead: 1
  };
  // Trade-catalog engagement/funnel events (assets/trade-catalog.js). Catalogue
  // PDF/CSV downloads and quote-request clicks are meaningful signals with no
  // other reader, so allow-list them through to AIM growth. Payloads are
  // non-PII (file name/type, request_type, source, counts).
  var CATALOG = {
    file_download: 1, catalog_request_quote: 1, trade_catalog_loaded: 1
  };
  // Top-level keys we never forward (PII / identity). journey-tracker prefixes
  // custom keys with `journey_`, so we also drop journey_customer_* below.
  var PII = {
    customer_id: 1, customer_email: 1, email: 1, phone: 1, customer_phone: 1,
    first_name: 1, last_name: 1, name: 1, address: 1, user_id: 1, user_data: 1
  };
  var MAX_BATCH = 25;
  var FLUSH_MS = 2500;

  // Consent: mirror third-party-loader's ktCanTrack(). Legacy default = undefined
  // (allowed) when no Shopify consent framework is present. In strict mode
  // (CFG.strictConsent, from settings.consent_mode_enabled) an unknown/absent API is
  // treated as DENIED, so nothing is sent before an explicit consent decision.
  // KT-192: read consent through window.KTConsent (snippets/kt-consent.liquid), which
  // is the only thing that loads Shopify's official consent-tracking API. Reading
  // window.Shopify.customerPrivacy here — as this did — found nothing on a returning
  // visit and a method-less stub (`{ cachedConsent, consentStatus }`) on the visit
  // where the banner renders, so canTrack() returned false under strict mode and
  // enqueue() dropped EVERY dataLayer event. The whole journey stream to AIMarketOps
  // was silently empty.
  function canTrack() {
    if (window.KTConsent && typeof window.KTConsent.canTrack === 'function') {
      var v = window.KTConsent.canTrack();
      return v === null ? undefined : v;
    }
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.userCanBeTracked === 'function') return cp.userCanBeTracked();
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return cp.analyticsProcessingAllowed();
    } catch (e) {}
    return CFG.strictConsent ? false : undefined;
  }

  // GA4 identifiers, read on-page from the first-party GA cookies so AIM can forward
  // the custom conversion events to GA4 server-side (Measurement Protocol) with correct
  // attribution. These are GA's own pseudonymous analytics IDs (not customer PII), and
  // they're only read inside flush(), which is gated on consent like everything else.
  function readCookie(name) {
    try {
      var re = new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)');
      var m = document.cookie.match(re);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }
  // _ga = "GA1.1.<clientId>" — the GA4 client_id is the last two dot-separated parts.
  // Prefer the real _ga so events stitch to the storefront's GA4 user; if it's absent
  // (KT's GA4 runs in a sandboxed Customer Events pixel that may not write a readable
  // top-level _ga), fall back to a stable first-party id so AIM's GA4 forward still
  // lands — it then attributes as a standalone GA4 user instead of being dropped.
  var CID_COOKIE = 'kt_ga4_cid';
  function ga4ClientId() {
    var ga = readCookie('_ga');
    if (ga) {
      var p = ga.split('.');
      if (p.length >= 4) return p.slice(-2).join('.');
    }
    return fallbackClientId();
  }
  // Stable first-party client_id in GA4's "<uint32>.<unix-seconds>" shape, persisted
  // for 2 years (same lifetime as _ga). Written only from here, inside the
  // consent-gated flush() path.
  function fallbackClientId() {
    var existing = readCookie(CID_COOKIE);
    if (existing) return existing;
    var cid = String(Math.floor(Math.random() * 2147483647)) + '.' + Math.floor(Date.now() / 1000);
    try {
      document.cookie = CID_COOKIE + '=' + cid + '; path=/; max-age=63072000; SameSite=Lax; Secure';
    } catch (e) {}
    return cid;
  }
  // ── Session cuts: how they arrived, and on what ────────────────────────────
  //
  // The funnel could only ever be read as one number because no event carried a
  // dimension to split it by. Two are added here, both derived ONCE per session
  // and cached in sessionStorage, so they describe the ENTRY to the visit rather
  // than whichever page happened to flush the batch.
  //
  // Deliberately coarse, because a funnel cut needs a bucket and not an
  // identity:
  //   - source is a UTM token or a REFERRER HOST. Never the referrer path or
  //     query, which would record the visitor's previous page.
  //   - device is a viewport bucket. No user-agent string and no capability
  //     probing, so it cannot contribute to a fingerprint.
  //
  // Both are read only inside the consent-gated flush(), like everything else.
  var SESSION_SOURCE_KEY = 'kt_journey_source';
  var SESSION_DEVICE_KEY = 'kt_journey_device';

  function sessionCache(key, derive) {
    try {
      var cached = sessionStorage.getItem(key);
      if (cached) return cached;
    } catch (e) {}
    var value = '';
    try { value = derive() || ''; } catch (e) { value = ''; }
    if (!value) return '';
    try { sessionStorage.setItem(key, value); } catch (e) {}
    return value;
  }

  function deriveSource() {
    // A campaign tag is the most specific answer and wins outright.
    try {
      var params = new URLSearchParams(location.search || '');
      var utm = params.get('utm_source');
      if (utm) return utm.toLowerCase().slice(0, 64);
    } catch (e) {}
    // Otherwise the referring host, with our own domain treated as internal
    // navigation rather than a source.
    var ref = '';
    try { ref = document.referrer || ''; } catch (e) { ref = ''; }
    if (!ref) return 'direct';
    try {
      var host = new URL(ref).hostname.replace(/^www\./, '').toLowerCase();
      if (!host) return 'direct';
      if (host === location.hostname.replace(/^www\./, '').toLowerCase()) return 'internal';
      return host.slice(0, 64);
    } catch (e) {
      return 'direct';
    }
  }

  function deriveDevice() {
    var w = 0;
    try { w = window.innerWidth || document.documentElement.clientWidth || 0; } catch (e) { w = 0; }
    if (!w) return 'unknown';
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  }

  function journeySource() { return sessionCache(SESSION_SOURCE_KEY, deriveSource); }
  function journeyDevice() { return sessionCache(SESSION_DEVICE_KEY, deriveDevice); }

  // _ga_<streamId> = "GS1.1.<sessionId>.<...>" — the session id is the 3rd part.
  function ga4SessionId() {
    try {
      var parts = document.cookie.split('; ');
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf('_ga_') === 0) {
          var val = parts[i].substring(parts[i].indexOf('=') + 1).split('.');
          if (val.length >= 3) return val[2];
        }
      }
    } catch (e) {}
    return '';
  }

  function isPii(key) {
    if (PII[key]) return true;
    // journey-tracker re-keys custom data as journey_<key>
    return key.indexOf('customer') !== -1 || key.indexOf('email') !== -1 || key.indexOf('phone') !== -1;
  }

  // Keep only the event name + safe scalar/array fields. Strings clipped so a
  // single beacon can never grow unbounded.
  function sanitize(entry) {
    if (!entry || typeof entry !== 'object') return null;
    var name = entry.event;
    if (typeof name !== 'string') return null;
    var isJourney = name.indexOf('kt_journey_') === 0;
    var isEcom = ECOMMERCE[name] === 1;
    var isLead = LEADS[name] === 1;
    var isCatalog = CATALOG[name] === 1;
    if (!isJourney && !isEcom && !isLead && !isCatalog) return null;

    var out = { event: name };
    for (var k in entry) {
      if (!Object.prototype.hasOwnProperty.call(entry, k)) continue;
      if (k === 'event' || k === 'gtm.uniqueEventId') continue;
      if (isPii(k)) continue;
      var v = entry[k];
      if (v == null) continue;
      if (typeof v === 'string') { out[k] = v.length > 256 ? v.slice(0, 256) : v; }
      else if (typeof v === 'number' || typeof v === 'boolean') { out[k] = v; }
      else if (k === 'ecommerce' && typeof v === 'object') {
        // GA4 ecommerce object — keep currency/value + item count only (no PII).
        out.ecommerce = {
          currency: typeof v.currency === 'string' ? v.currency : undefined,
          value: typeof v.value === 'number' ? v.value : undefined,
          item_count: Array.isArray(v.items) ? v.items.length : undefined
        };
      }
    }
    return out;
  }

  var buffer = [];
  var timer = null;

  function flush() {
    if (timer) { clearTimeout(timer); timer = null; }
    if (!buffer.length) return;
    if (canTrack() === false) { buffer = []; return; } // consent withdrawn — drop
    var events = buffer.splice(0, MAX_BATCH);
    var payload;
    try {
      payload = JSON.stringify({
        shop: SHOP,
        path: location.pathname || '',
        events: events,
        // GA4 client_id/session so AIM can forward the custom events to GA4 with correct
        // attribution. Omitted (keys dropped) when the GA cookies aren't present.
        client_id: ga4ClientId() || undefined,
        ga_session_id: ga4SessionId() || undefined,
        // Session-level funnel cuts (see journeySource/journeyDevice above).
        source: journeySource() || undefined,
        device: journeyDevice() || undefined
      });
    } catch (e) { return; }
    try {
      // text/plain keeps it a "simple" CORS request (no preflight), same as the
      // ai-visit beacon, so sendBeacon works cross-origin to the AIM domain.
      if (navigator.sendBeacon) {
        var blob = new Blob([payload], { type: 'text/plain' });
        if (navigator.sendBeacon(URL_, blob)) return;
      }
      fetch(URL_, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: payload, keepalive: true, mode: 'cors' }).catch(function () {});
    } catch (e) {}
  }

  // KT-192: hold events while consent is still UNRESOLVED rather than discarding them.
  // The consent API resolves a few hundred ms into the page, and everything the theme
  // pushes before that (page_view, view_item, the initial ecommerce payload) used to be
  // thrown away — so even a fully consented visitor lost the front of every session.
  // Held events are still only ever SENT from flush(), which re-checks consent, and are
  // dropped outright the moment consent comes back denied.
  var pending = [];
  var MAX_PENDING = 50;

  function releasePending() {
    if (!pending.length) return;
    if (canTrack() === false) { pending = []; return; }
    var held = pending;
    pending = [];
    for (var i = 0; i < held.length; i++) accept(held[i]);
  }

  function accept(clean) {
    buffer.push(clean);
    if (buffer.length >= MAX_BATCH) { flush(); return; }
    if (!timer) timer = setTimeout(flush, FLUSH_MS);
  }

  function enqueue(entry) {
    var clean = sanitize(entry);
    if (!clean) return;
    // Unresolved consent under strict mode is reported as `false` by canTrack(), so
    // "unresolved" is detected from KTConsent's own `known` flag, not from canTrack().
    if (!consentKnown()) {
      if (pending.length < MAX_PENDING) pending.push(clean);
      return;
    }
    if (canTrack() === false) return; // denied — never buffered, never sent
    accept(clean);
  }

  function consentKnown() {
    if (window.KTConsent && typeof window.KTConsent.state === 'function') {
      return window.KTConsent.state().known === true;
    }
    return true; // no KTConsent — legacy path, treat every decision as final
  }

  try {
    window.dataLayer = window.dataLayer || [];
    // Process anything already queued before this script ran.
    for (var i = 0; i < window.dataLayer.length; i++) enqueue(window.dataLayer[i]);
    // Tap future pushes without disturbing any other consumer.
    var origPush = window.dataLayer.push;
    window.dataLayer.push = function () {
      var r = origPush.apply(this, arguments);
      try { for (var j = 0; j < arguments.length; j++) enqueue(arguments[j]); } catch (e) {}
      return r;
    };
  } catch (e) { return; }

  // Release (or discard) held events as soon as consent resolves, and react to a
  // later change of mind.
  if (window.KTConsent && typeof window.KTConsent.onChange === 'function') {
    window.KTConsent.onChange(function (state) {
      if (!state.known) return;
      if (state.canTrack === false) { pending = []; buffer = []; return; }
      releasePending();
    });
  }

  // Flush on the way out so the last events aren't lost.
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') { releasePending(); flush(); } });
  window.addEventListener('pagehide', function () { releasePending(); flush(); });
})();
